import React, { useEffect, useState, useRef } from "react";
import api from "../../axios/api";
import {
  FaCheckCircle,
  FaInstagram,
  FaMapMarkerAlt,
  FaTree,
  FaUsers,
  FaUserCircle,
  FaCalendarAlt,
  FaUserAlt,
  FaCalendarPlus,
  FaClock,
  FaPagelines,
} from "react-icons/fa";
import ReactLoading from "react-loading";
import "../../assets/NewFiles/Css/searchcomponent.css";
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
} from "react-share";
import {
  EmailIcon,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from "react-share";
import { Pagination, Box, Select, MenuItem } from "@mui/material";

const SearchComponent = () => {
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const requestIdRef = useRef(0);
  const [filters, setFilters] = useState({
    treeName: true, // Default search by tree name
    uploadedBy: false,
    location: false,
    treeId: false,
  });
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [defaultPaginationValue, setDefaultPaginationValue] = useState(10);

  // Debounce search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  //  Fetch Pagination Settings from Admin
  const fetchPaginationSettings = async () => {
    try {
      const response = await api.get("api/upload/getPaginationSettings?active=1");
      if (response.data.status && response.data.data?.options) {
        const options = response.data.data.options.map((opt) => Number(opt.VALUE));
        const defaultVal = Number(response.data.data.default);
        setPaginationOptions(options);
        setDefaultPaginationValue(defaultVal);
        setRowsPerPage(defaultVal);
      }
    } catch (error) {
      console.error("❌ Error fetching pagination settings:", error);
    }
  };

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

 
  useEffect(() => {
    fetchData();
  }, [page, debouncedSearchTerm, filters, rowsPerPage]);

  
  useEffect(() => {
    fetchPaginationSettings();
  }, []);


  const fetchData = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const offset = (page - 1) * rowsPerPage;

      const params = new URLSearchParams({
        page,
        limit: rowsPerPage,
        offset,
        searchTerm: debouncedSearchTerm,
        searchByTreeName: filters.treeName,
        searchByUploadedBy: filters.uploadedBy,
        searchByLocation: filters.location,
        searchByTreeId: filters.treeId,
      });

      const response = await api.get(`/api/upload/all-images?${params}`);

      // If a newer fetchData call has been fired since this one started,
      // this response is stale — discard it instead of overwriting fresher state.
      if (requestId !== requestIdRef.current) return;

      setData(response.data.images);
      setStats(response.data.stats);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };


  const handleInputChange = (e) => {
    setSearchTerm(e.target.value.trim());
    setPage(1); 
  };


  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilters({
      ...filters,
      [name]: checked,
    });
    setPage(1); 
  };


  const handlePageChange = (event, value) => {
    setPage(value);
  };


  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1); 
  };


  const handleShareClick = (treeId) => {
    const url = `${window.location.origin}/plantprofile/${treeId}`;
    setShareUrl(url);
    setIsShareModalOpen(true);
  };


  if (loading && page === 1) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
        <div style={{ marginLeft: '20px' }}>
          <p>Loading tree list...</p>
        </div>
      </div>
    );
  }


  const ShareModal = ({ url, onClose }) => (
    <div className="share-modal">
      <div className="share-modal-content">
        <h3>Share this tree</h3>
        <div className="share-buttons">
          <FacebookShareButton url={url}>
            <FacebookIcon size={32} round />
          </FacebookShareButton>
          <TwitterShareButton url={url}>
            <TwitterIcon size={32} round />
          </TwitterShareButton>
          <button
            onClick={() => {
              navigator.clipboard
                .writeText(url)
                .then(() => {
                  const shareUrl = `https://www.instagram.com/`;
                  window.open(shareUrl, "_blank");
                })
                .catch((err) => {
                  console.error("Failed to copy: ", err);
                });
            }}
            style={{
              height: "32px",
              width: "32px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "50%",
              padding: "0",
              backgroundColor: "#D93F7C",
            }}
          >
            <FaInstagram size={20} />
          </button>
          <WhatsappShareButton url={url}>
            <WhatsappIcon size={32} round />
          </WhatsappShareButton>
          <LinkedinShareButton url={url}>
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>
          <EmailShareButton url={url}>
            <EmailIcon size={32} round />
          </EmailShareButton>
        </div>

        <input type="text" value={url} readOnly />
        <button
          onClick={() => {
            navigator.clipboard.writeText(url);
            alert("Link copied!");
          }}
        >
          Copy Link
        </button>

        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <div className="search-component">
      <div className="search-container">
        <div className="search-row">
          <div className="search-col">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleInputChange}
              className="search-input"
            />
          </div>
          <div className="search-colII">
            <div className="filters">
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="treeName"
                    checked={filters.treeName}
                    onChange={handleCheckboxChange}
                    className="checkbox-input"
                  />
                  Tree Name
                </label>
                <label >
                  <input
                    type="checkbox"
                    name="uploadedBy"
                    checked={filters.uploadedBy}
                    onChange={handleCheckboxChange}
                    className="checkbox-input"
                  />
                  Uploaded By
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="location"
                    checked={filters.location}
                    onChange={handleCheckboxChange}
                    className="checkbox-input"
                  />
                  Location
                </label>
                <label>
              <input
                type="checkbox"
                name="treeId"
                checked={filters.treeId}
                onChange={handleCheckboxChange}
                className="checkbox-input"
              />
              Tree ID
                </label>
                <label className="checkbox-label" style={{ gap: "5px" }}>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={rowsPerPage}
                    label="rowsPerPage"
                    onChange={handleRowsPerPageChange}
                    sx={{ height: 30 }}
                  >
                    {paginationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  Trees Per page
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Centering stats */}
      <div className="stats-container">
        <div className="stat-item">
          <FaTree className="stat-icon" />
          <span className="stat-text">
            Total Plants: {stats.totalTrees || 0}
          </span>
        </div>
        <div className="stat-item">
          <FaCheckCircle className="stat-icon" />
          <span className="stat-text">
            Verified Plants: {stats.verifiedTrees || 0}
          </span>
        </div>
        <div className="stat-item">
          <FaUsers className="stat-icon" />
          <span className="stat-text">
            Total Users: {stats.totalUsers || 0}
          </span>
        </div>
        <div className="stat-item">
          <FaUserCircle className="stat-icon" />
          <span className="stat-text">
            Verified Users: {stats.verifiedUsers || 0}
          </span>
        </div>
      </div>

      {/* Loading overlay when changing pages */}
      {loading && page > 1 && (
        <div className="overlay-loading">
          <ReactLoading type="spin" color="#8BC34A" height={40} width={40} />
        </div>
      )}

      {/* Card section */}
      <section className="search-card-section">
        <div className="search-card-container">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 mb-8">
            {data.length > 0 ? (
              data.map((item, index) => (
                <div key={index} className="search-card-col">
                  <div className="search-blogCardItem">
                    <div className="search-blogCardImage">
                      <a
                        target="_blank"
                        className="search-more-info-btn"
                        href={`/plantprofile/${item.id}`}
                        rel="noreferrer"
                      >
                        <img
                          src={item.treeImageUrl}
                          alt={item.treeName}
                          className="search-card-image"
                          title="View-More"
                          loading="lazy"
                        />
                      </a>
                    </div>
                    <div>
                      <span className="search-card-plantName" title="View-More">
                        <span className="search-verified">
                          {item.isVerified ? (
                            <FaCheckCircle color="green" />
                          ) : (
                            <FaCheckCircle color="yellow" />
                          )}
                        </span>
                        <a
                          target="_blank"
                          className="search-more-info-btn"
                          href={`/plantprofile/${item.id}`}
                          rel="noreferrer"
                        >
                          &nbsp;&nbsp;{item.treeName}
                        </a>
                      </span>

                      <div className="search-blogCardDetails">
                        <span className="search-card-detail" title="Tree-Name">
                          <FaTree
                            style={{
                              display: "inline-flex",
                              marginBottom: "2px",
                            }}
                            color={"#002D62"}
                            size={14}
                            className="mt-1.5"
                          />{" "}
                          {item.tree_id}
                        </span>
                        <span className="search-card-detail" title="Uploaded-By">
                          <FaUserAlt
                            style={{
                              display: "inline-flex",
                              marginBottom: "2px",
                            }}
                            color={"#002D62"}
                            size={14}
                            className="mt-1.5"
                          />{" "}
                          {item.userName}
                        </span>
                        <span className="search-card-detail" title="Uploaded-On">
                          <FaCalendarPlus
                            style={{ display: "inline-flex" }}
                            color={"#002D62"}
                            size={14}
                            className="mt-1.5"
                          />{" "}
                          {new Date(item.createdAt).toDateString()}
                        </span>
                        <span className="search-card-detail" title="Planted-On">
                          <FaCalendarAlt
                            style={{ display: "inline-flex" }}
                            color={"#002D62"}
                            size={14}
                            className="mt-1.5"
                          />{" "}
                          {new Date(item.plantedOn).toDateString()}
                        </span>
                        <span
                          className="search-card-detail hoverable"
                          title={`Species : ${item.common_name}`}
                        >
                          <FaPagelines
                            style={{ display: "inline-flex" }}
                            color={"#002D62"}
                            size={14}
                            className="mt-1.5"
                          />{" "}
                          {item.speciesLabel}
                        </span>
                        <span
                          className="search-card-detail hoverable"
                          title="Age"
                        >
                          <FaClock
                            style={{ display: "inline-flex" }}
                            color={"#002D62"}
                            className="mt-1.5"
                            size={14}
                          />{" "}
                          {item.age}
                        </span>
                        <span
                          className="search-card-detail hoverable truncate-multiline"
                          title={`Location: ${item.address}`}
                        >
                          <FaMapMarkerAlt
                            style={{ display: "inline-flex" }}
                            color={"#002D62"}
                            size={14}
                          />{" "}
                          {item.address}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <p>No plants found matching your search criteria.</p>
              </div>
            )}
          </div>
        </div>
      </section>
      {isShareModalOpen && (
        <ShareModal url={shareUrl} onClose={() => setIsShareModalOpen(false)} />
      )}
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          className="pagination-container"
          showFirstButton
          showLastButton
        />
      </Box>
    </div>
  );
};

export default SearchComponent;

const styles = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
};
